import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Button,
  Input,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Divider,
  Alert,
  AlertIcon,
  Spinner,
  Image,
  Flex,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiDownload,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiCopy,
  FiExternalLink,
  FiCode,
} from 'react-icons/fi';
import { api } from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';

interface QRCode {
  id: number;
  name: string;
  description?: string;
  target_url: string;
  qr_code_url: string;
  scan_count: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

const QRCodesAdminPage: React.FC = () => {
  const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingQR, setEditingQR] = useState<QRCode | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_url: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await api.get<QRCode[]>('/admin/qr-codes');
      setQRCodes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
      toast({
        title: 'Chyba při načítání',
        description: 'Nepodařilo se načíst QR kódy',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.target_url) {
      toast({
        title: 'Chyba',
        description: 'Název a cílová URL jsou povinné',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingQR) {
        await api.put(`/admin/qr-codes/${editingQR.id}`, formData);
        toast({
          title: 'QR kód aktualizován',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await api.post('/admin/qr-codes', formData);
        toast({
          title: 'QR kód vytvořen',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      
      setIsCreateModalOpen(false);
      setIsEditModalOpen(false);
      setEditingQR(null);
      setFormData({ name: '', description: '', target_url: '' });
      fetchQRCodes();
    } catch (error) {
      console.error('Failed to save QR code:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se uložit QR kód',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (qr: QRCode) => {
    setEditingQR(qr);
    setFormData({
      name: qr.name,
      description: qr.description || '',
      target_url: qr.target_url,
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Opravdu chcete smazat tento QR kód?')) return;

    try {
      await api.delete(`/admin/qr-codes/${id}`);
      toast({
        title: 'QR kód smazán',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      fetchQRCodes();
    } catch (error) {
      console.error('Failed to delete QR code:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se smazat QR kód',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadQR = async (qr: QRCode) => {
    try {
      const response = await fetch(qr.qr_code_url);
      if (!response.ok) {
        throw new Error('Failed to fetch QR code image');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-${qr.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download QR code:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se stáhnout QR kód',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Zkopírováno',
        description: 'URL zkopírována do schránky',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se zkopírovat URL',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Načítám QR kódy...</Text>
          </VStack>
        </Flex>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between">
        <Heading size="lg">QR kódy</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Vytvořit QR kód
        </Button>
      </HStack>

      {/* Info Alert */}
      <Alert status="info">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">QR kódy pro rychlý přístup</Text>
          <Text fontSize="sm">
            Vytvářejte QR kódy pro různé stránky vašeho webu - články, kontakty, sponzory atd.
            Uživatelé mohou jednoduše naskenovat kód a okamžitě navštívit cílovou stránku.
          </Text>
        </Box>
      </Alert>

      {/* QR Codes Grid */}
      {qrCodes.length === 0 ? (
        <Card>
          <CardBody textAlign="center" py={12}>
            <FiCode size={48} color="gray.400" />
            <VStack spacing={4} mt={6}>
              <Heading size="md" color="gray.600">Žádné QR kódy</Heading>
              <Text color="gray.500">Ještě nebyly vytvořeny žádné QR kódy</Text>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                onClick={() => setIsCreateModalOpen(true)}
              >
                Vytvořit první QR kód
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {qrCodes.map((qr) => (
            <Card key={qr.id} borderWidth={1} borderRadius="lg">
              <CardBody>
                <VStack spacing={4}>
                  {/* QR Code Image */}
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="gray.200"
                  >
                    <Image
                      src={qr.qr_code_url}
                      alt={`QR kód: ${qr.name}`}
                      w={150}
                      h={150}
                      fallback={<Box w={150} h={150} bg="gray.100" />}
                    />
                  </Box>

                  {/* QR Info */}
                  <VStack spacing={2} align="start" width="100%">
                    <HStack justify="space-between" width="100%">
                      <Text fontWeight="bold" fontSize="lg" noOfLines={1}>
                        {qr.name}
                      </Text>
                      <Badge colorScheme={qr.is_active ? 'green' : 'gray'}>
                        {qr.is_active ? 'Aktivní' : 'Neaktivní'}
                      </Badge>
                    </HStack>
                    
                    {qr.description && (
                      <Text fontSize="sm" color="gray.600" noOfLines={2}>
                        {qr.description}
                      </Text>
                    )}

                    <Text fontSize="sm" color="blue.600" noOfLines={1}>
                      {qr.target_url}
                    </Text>

                    <HStack spacing={4} fontSize="sm" color="gray.500">
                      <Text>📊 {qr.scan_count} skenování</Text>
                      <Text>📅 {new Date(qr.created_at).toLocaleDateString('cs-CZ')}</Text>
                    </HStack>
                  </VStack>

                  {/* Actions */}
                  <VStack spacing={2} width="100%">
                    <HStack spacing={2} width="100%">
                      <Tooltip label="Stáhnout QR kód">
                        <IconButton
                          aria-label="Stáhnout"
                          icon={<FiDownload />}
                          size="sm"
                          onClick={() => handleDownloadQR(qr)}
                        />
                      </Tooltip>
                      <Tooltip label="Kopírovat URL">
                        <IconButton
                          aria-label="Kopírovat"
                          icon={<FiCopy />}
                          size="sm"
                          onClick={() => copyToClipboard(qr.target_url)}
                        />
                      </Tooltip>
                      <Tooltip label="Otevřít v novém okně">
                        <IconButton
                          aria-label="Otevřít"
                          icon={<FiExternalLink />}
                          size="sm"
                          as="a"
                          href={qr.target_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      </Tooltip>
                      <Box flex={1} />
                      <Tooltip label="Upravit">
                        <IconButton
                          aria-label="Upravit"
                          icon={<FiEdit />}
                          size="sm"
                          onClick={() => handleEdit(qr)}
                        />
                      </Tooltip>
                      <Tooltip label="Smazat">
                        <IconButton
                          aria-label="Smazat"
                          icon={<FiTrash2 />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDelete(qr.id)}
                        />
                      </Tooltip>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Create Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Vytvořit QR kód</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Název</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Např. Kontaktní stránka"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Popis</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Krátký popis účelu QR kódu"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Cílová URL</FormLabel>
                  <Input
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    placeholder="https://vasweb.cz/stranka"
                    type="url"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => setIsCreateModalOpen(false)}
              >
                Zrušit
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
              >
                Vytvořit
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upravit QR kód</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Název</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Např. Kontaktní stránka"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Popis</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Krátký popis účelu QR kódu"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Cílová URL</FormLabel>
                  <Input
                    value={formData.target_url}
                    onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                    placeholder="https://vasweb.cz/stranka"
                    type="url"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="outline"
                mr={3}
                onClick={() => setIsEditModalOpen(false)}
              >
                Zrušit
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
              >
                Uložit změny
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
      </VStack>
    </AdminLayout>
  );
};

export default QRCodesAdminPage;
